import { useMemo } from "react";
import Chart from "react-apexcharts";

import { fontFamily } from 'utils/typography';
import Card, { CardLayoutProps } from 'components/Card';

import LabelItem from './LabelItem';

import { WellbeingRow, getOverallScore } from '../data';

type Props = CardLayoutProps & { wellbeingRow: WellbeingRow; };

const DailyOverview = ({ className, wellbeingRow }: Props) => {
  const series = useMemo(() => ([
    wellbeingRow?.stressFree || 0,
    wellbeingRow?.anxietyFree || 0,
    wellbeingRow?.happiness || 0,
    wellbeingRow?.angerFree || 0,
  ]), [wellbeingRow]);

  const options: ApexCharts.ApexOptions = {
    colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#872D8D'],
    labels: ['Stress-free', 'Anxiety-free', 'Happiness', 'Calmness'],
    plotOptions: {
      radialBar: {
        offsetY: -20,
        dataLabels: {
          name: {
            fontSize: '14px',
            fontWeight: 400,
            fontFamily: fontFamily,
            offsetY: 15,
          },
          value: {
            fontSize: '16px',
            fontWeight: 700,
            fontFamily: fontFamily,
            offsetY: -20,
            formatter: function (value: number) {
              return value ? `${value}%` : '';
            },
          },
          total: {
            show: true,
            fontSize: '14px',
            label: 'Wellness score',
            fontWeight: 400,
            fontFamily: fontFamily,
            color: '#000000',
            formatter: function (options: any) {
              const currentSeries = options?.config?.series || [];

              const wellnessScore = getOverallScore({
                happiness: currentSeries[2],
                angerFree: currentSeries[3],
                stressFree: currentSeries[0],
                anxietyFree: currentSeries[1],
              });

              return !wellnessScore || wellnessScore < 1
                ? ''
                : `${wellnessScore}%`;
            },
          },
        },
      },
    },
  };

  return (
    <Card className={className}>
      <div className="p-2 xxs:p-5 grid">
        <span className="font-bold text-black">
          Daily overview
        </span>
        <div className="pt-7 flex flex-wrap justify-center items-center m-auto gap-5 lg:gap-0 xl:gap-5">
          <div style={{maxWidth: '250px', maxHeight: '250px' }}>
            <Chart
              type="radialBar"
              height="315px"
              series={series}
              options={options}
            />
          </div>
          <div
            className="flex justify-center"
            style={{ width: '195px' }}
          >
            <ul 
              className="grid gap-1.5 sm:gap-3"
              style={{ width: '129px' }}
            >
              <LabelItem
                label="Stress-free"
                value={wellbeingRow?.stressFree}
                backgroundColor="#249EFA"
              />
              <LabelItem
                label="Anxiety-free"
                value={wellbeingRow?.anxietyFree}
                backgroundColor="#24E6A4"
              />
              <LabelItem
                label="Happiness"
                value={wellbeingRow?.happiness}
                backgroundColor="#FDBA3A"
              />
              <LabelItem
                label="Calmness"
                value={wellbeingRow?.angerFree}
                backgroundColor="#FD5F76"
              />
            </ul>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default DailyOverview;
